import {Color} from '@benchling/nucleus-ui';
import {MantineThemeColors, useMantineTheme} from '@mantine/core';
import React from 'react';
import {BaseEdge, EdgeProps, getBezierPath} from 'reactflow';

import {
  STATUS_COLORS,
  EdgeStatus,
} from 'ui-library/reactflow/nodes/BenchlingNode/v2/BenchlingNodeV2Constants';

export enum LineStyle {
  SOLID = 'SOLID',
  DASHED = 'DASHED',
}
interface BaseEdgeRendererData {
  color?: string;
  lineStyle?: LineStyle;
  status?: EdgeStatus;
}

const DEFAULT_EDGE_OPTIONS = {
  color: Color.GRAY_400,
  lineStyle: LineStyle.SOLID,
};
const SELECTION_COLOR = Color.BLUE_450;

/** @package */
export function getStrokeColor(
  theme: {
    colors: MantineThemeColors;
  },
  overrideColor?: string,
  status?: EdgeStatus,
  selected?: boolean
): string {
  if (selected) {
    return SELECTION_COLOR;
  }
  if (status) {
    const statusColor = STATUS_COLORS[status];
    return theme.colors[statusColor.color][statusColor.colorIndex];
  }
  return overrideColor ?? DEFAULT_EDGE_OPTIONS.color;
}

/**
 * This is a simple bezier edge renderer which can be used for many simple edge types.
 * It renders a bezier curve between two nodes with a default style.
 */
export default function BaseEdgeRenderer({
  data = DEFAULT_EDGE_OPTIONS,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  markerEnd,
  selected,
  label,
}: EdgeProps<BaseEdgeRendererData>): JSX.Element {
  const theme = useMantineTheme();
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const edgePathStyle = {
    stroke: getStrokeColor(theme, data.color, data.status, selected),
    strokeWidth: '1.5px',
    strokeDasharray: data.lineStyle === LineStyle.DASHED ? '2 3' : undefined,
  };

  return (
    <BaseEdge
      path={edgePath}
      markerEnd={markerEnd}
      style={edgePathStyle}
      label={label}
      labelX={labelX}
      labelY={labelY}
    />
  );
}
